import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import { TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { useToast } from '@wartek-id/toast'

import CustomTabList from '../../components/CustomTabList'
import { tabNamesV2, tabNamesV3 } from 'app/KinerjaPage/constants'
import { apiPaths, useAPIUpsertSKP } from 'api/kinerja/useAPIKinerja'
import DialogSaveDraft from 'app/KinerjaPage/components/DialogSaveDraft'
import TabPraktikPembelajaran from './TabPraktikPembelajaran'
import TabPengembanganKompetensi from './TabPengembanganKompetensi'
import TabPerilakuKerja from './TabPerilakuKerja'
import TabRangkuman from './TabRangkuman'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'
import TabTugasPokok from './TabTugasPokok'
import useToggleSdkContext from 'utils/useToogleSdk'
import TabTugasTambahan from './TabTugasTambahan'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'

import type {
  Kinerja,
  KinerjaTeachingRoleType,
  SKPTabHandler,
  UpdateSKPPayload,
} from 'types/kinerja'

interface KinerjaPerencanaanAppProps {
  skp: Kinerja
  isSKPFetched: boolean
  role?: KinerjaTeachingRoleType
}

enum TabIndexOld {
  PRAKTIK_PEMBELAJARAN = 0,
  PENGEMBANGAN_KOMPENTESI = 1,
  TUGAS_TAMBAHAN = 2,
  PERILAKU_KERJA = 3,
  RANGKUMAN = 4,
}

enum TabIndex {
  TUGAS_POKOK = 0,
  PRAKTIK_KINERJA = 1,
  PENGEMBANGAN_KOMPETENSI = 2,
  PERILAKU_KERJA = 3,
  RANGKUMAN = 4,
}

function KinerjaPerencanaanApp({
  skp,
  isSKPFetched,
  role = 'GURU',
}: KinerjaPerencanaanAppProps) {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const toggles = useToggleSdkContext()
  const { trackEvent } = useSKPTrackEvent()

  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const isTugasPokokEnabled =
    toggles?.['kinerja-tugas-pokok-enabled'] === true && skp?.tahun > 2024

  const isSPNFEnabled = toggles?.['kinerja-spnf-enabled'] === true

  const [activeTabIndex, setActiveTabIndex] = useState<TabIndex | TabIndexOld>(
    TabIndexOld.PRAKTIK_PEMBELAJARAN
  )

  const [isShowDialogSaveDraft, setIsShowDialogSaveDraft] = useState(false)

  const { mutateAsync: mutateSKP, isLoading: isUpdatingSKP } = useAPIUpsertSKP()
  const queryKeys = useGetKinerjaQuerykeys(apiPaths.kinerja())

  const isSKPApproved = skp?.status === 'APPROVED'

  useEffect(() => {
    if (skp?.status === 'NOT_FOUND') {
      router.push(`${baseUrl}/konfirmasi-profil`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skp])

  const goToHome = () => {
    router.push(baseUrl)
  }

  const handleClickBack = () => {
    if (skp?.status === 'DRAFT') {
      setIsShowDialogSaveDraft(true)
    } else {
      goToHome()
    }
  }

  const tabHandlerRef = useRef<SKPTabHandler>()
  const tabNames = isTugasPokokEnabled ? tabNamesV3 : tabNamesV2

  const handleError = (error: any) => {
    console.error(error)

    const message =
      error?.originalError?.response?.data?.error?.message ?? error.toString()

    toast({
      message: `Gagal mengubah rencana hasil kerja: ${message}`,
    })
  }

  const updateSKP = async (data: UpdateSKPPayload) => {
    const payload = {
      ...data,
      ...(isSPNFEnabled ? { role } : {}),
    }
    await mutateSKP(payload)
    await queryClient.invalidateQueries({
      queryKey: queryKeys,
    })
  }

  const handleSaveDraft = async (data: UpdateSKPPayload) => {
    try {
      await updateSKP(data)
      goToHome()

      toast({
        message: 'Berhasil simpan draf!',
        icon: <Icon color="inverse">check</Icon>,
      })
    } catch (error) {
      handleError(error)
    }
  }

  const handleClickNext = async (data: UpdateSKPPayload, tabIndex: number) => {
    try {
      await updateSKP(data)

      setActiveTabIndex(tabIndex)
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <div className="py-[50px] px-[116px]">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {/*
           * Note: we need to explicitly set font-size and line-height as workaround of styles collision
           * due to multiple version of @wartek-id/icon being used in this project.
           */}
          <Icon
            className="text-black !text-4xl !leading-9"
            fontSize="lg"
            data-testid="icon-back"
            onClick={handleClickBack}
          >
            arrow_back
          </Icon>
          <Text variant="display-md">Perencanaan Kinerja</Text>
        </div>
      </div>

      {!isSKPFetched ? (
        <Shimmer data-testid="loading-spinner-skp" />
      ) : (
        <div className="bg-white rounded-md mt-8 mb-[78px]">
          <Tabs onChange={setActiveTabIndex} selectedIndex={activeTabIndex}>
            <CustomTabList
              activeTabIndex={activeTabIndex}
              tabs={!isSKPApproved ? tabNames : tabNames.slice(0, 4)}
              disableInactiveTab
            />
            <TabPanels>
              {isTugasPokokEnabled && (
                <TabPanel>
                  <TabTugasPokok
                    ref={tabHandlerRef}
                    skp={skp}
                    onClickSave={handleSaveDraft}
                    isSubmitting={isUpdatingSKP}
                    onClickNext={(data) =>
                      handleClickNext(data, TabIndex.PRAKTIK_KINERJA)
                    }
                  />
                </TabPanel>
              )}
              <TabPanel>
                <TabPraktikPembelajaran
                  ref={tabHandlerRef}
                  skp={skp}
                  isSubmitting={isUpdatingSKP}
                  onClickSave={(data) => {
                    trackEvent({
                      event_name: 'CLICK_KINERJA_DRAFT_PLANNING',
                      screen_name: 'KINERJA_PLANNING_PRAKTIK_PEMBELAJARAN_PAGE',
                      kinerja_planning_id: skp?.id,
                      kinerja_planning_status: skp?.status,
                    })
                    handleSaveDraft(data)
                  }}
                  onClickNext={(data) => {
                    trackEvent({
                      event_name:
                        'CLICK_KINERJA_PLANNING_TO_PENGEMBANGAN_KOMPETENSI',
                      screen_name: 'KINERJA_PLANNING_PRAKTIK_PEMBELAJARAN_PAGE',
                      kinerja_planning_id: skp?.id,
                      kinerja_planning_status: skp?.status,
                    })
                    handleClickNext(
                      data,
                      isTugasPokokEnabled
                        ? TabIndex.PENGEMBANGAN_KOMPETENSI
                        : TabIndexOld.PENGEMBANGAN_KOMPENTESI
                    )
                  }}
                />
              </TabPanel>
              <TabPanel>
                <TabPengembanganKompetensi
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={(data) => {
                    trackEvent({
                      event_name: 'CLICK_KINERJA_DRAFT_PLANNING',
                      screen_name:
                        'KINERJA_PLANNING_PENGEMBANGAN_KOMPETENSI_PAGE',
                      kinerja_planning_id: skp?.id,
                      kinerja_planning_status: skp?.status,
                    })
                    handleSaveDraft(data)
                  }}
                  onClickNext={(data) => {
                    trackEvent({
                      event_name: 'CLICK_KINERJA_PLANNING_TO_TUGAS_TAMBAHAN',
                      screen_name:
                        'KINERJA_PLANNING_PENGEMBANGAN_KOMPETENSI_PAGE',
                      kinerja_planning_id: skp?.id,
                      kinerja_planning_status: skp?.status,
                    })
                    handleClickNext(
                      data,
                      isTugasPokokEnabled
                        ? TabIndex.PERILAKU_KERJA
                        : TabIndexOld.TUGAS_TAMBAHAN
                    )
                  }}
                  isSubmitting={isUpdatingSKP}
                />
              </TabPanel>
              {!isTugasPokokEnabled && (
                <TabPanel>
                  <TabTugasTambahan
                    ref={tabHandlerRef}
                    skp={skp}
                    onClickSave={handleSaveDraft}
                    onClickNext={(data) =>
                      handleClickNext(data, TabIndexOld.PERILAKU_KERJA)
                    }
                    isSubmitting={isUpdatingSKP}
                  />
                </TabPanel>
              )}
              <TabPanel>
                <TabPerilakuKerja
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  isLoading={isUpdatingSKP}
                  onClickNext={(data) =>
                    handleClickNext(
                      data,
                      isTugasPokokEnabled
                        ? TabIndex.RANGKUMAN
                        : TabIndexOld.RANGKUMAN
                    )
                  }
                />
              </TabPanel>
              <TabPanel>
                <TabRangkuman
                  ref={tabHandlerRef}
                  skp={skp}
                  role={role}
                  isSubmitting={isUpdatingSKP}
                  onRequestChange={() =>
                    setActiveTabIndex(
                      isTugasPokokEnabled
                        ? TabIndex.TUGAS_POKOK
                        : TabIndexOld.PRAKTIK_PEMBELAJARAN
                    )
                  }
                  onClickBackToHome={goToHome}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      )}

      <DialogSaveDraft
        data-testid="dialog-save-draft"
        isOpen={isShowDialogSaveDraft}
        isLoading={isUpdatingSKP}
        onClose={() => setIsShowDialogSaveDraft(false)}
        onClickCancel={() => setIsShowDialogSaveDraft(false)}
        onClickConfirm={() => {
          tabHandlerRef.current?.saveDraft()
        }}
      />
    </div>
  )
}

export default KinerjaPerencanaanApp
